/**
 * Hook for AI content generation functionality.
 */

import { useState, useCallback } from "react";
import { apiPost, apiGet } from "../lib/api";
import type {
  DisciplineSlug,
  SkillKey,
  ContentGenerationRequest,
  ContentGenerationResponse,
  GeneratedChallengeContent,
  ChallengeType,
} from "@jumpbean/shared";

// ----- Types -----

export interface GenerationFormState {
  discipline: DisciplineSlug | "";
  level: number;
  challengeType: ChallengeType | "";
  count: number;
  targetSkills: SkillKey[];
  customInstructions: string;
}

export interface GenerationResult {
  challenges: GeneratedChallengeContent[];
  metadata: ContentGenerationResponse["metadata"];
  allSkillTags: SkillKey[];
}

export interface ChallengeAcceptState {
  [index: number]: boolean;
}

interface SaveChallengesRequest {
  challenges: Array<{
    levelId: string;
    challengeNumber: number;
    type: ChallengeType;
    content: Record<string, unknown>;
    difficulty: number;
    skillTags: string[];
    aiMetadata: Record<string, unknown>;
    reviewStatus: string;
    isActive: boolean;
  }>;
  generationId?: string;
}

interface SaveChallengesResponse {
  success: boolean;
  data?: {
    createdIds: string[];
    count: number;
  };
  error?: string;
}

interface LevelInfo {
  id: string;
  levelNumber: number;
  name: string;
  disciplineId: string;
}

interface LevelsResponse {
  success: boolean;
  data: LevelInfo[];
}

// ----- Challenge Types by Discipline -----

export const ChallengeTypesByDiscipline: Record<DisciplineSlug, ChallengeType[]> = {
  math: ["counting", "number_recognition", "addition", "subtraction", "comparison"],
  spelling: ["letter_recognition", "beginning_sound", "word_building"],
  patterns: ["pattern_sequence", "color_pattern"],
  typing: ["key_finding", "sequence_typing", "word_typing"],
  programming: ["maze", "single_command"],
};

// ----- Initial State -----

const initialFormState: GenerationFormState = {
  discipline: "",
  level: 1,
  challengeType: "",
  count: 3,
  targetSkills: [],
  customInstructions: "",
};

// ----- Hook -----

export function useAIContentGeneration() {
  const [formState, setFormState] = useState<GenerationFormState>(initialFormState);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null);
  const [acceptedChallenges, setAcceptedChallenges] = useState<ChallengeAcceptState>({});

  // Update form field
  const updateField = useCallback(
    <K extends keyof GenerationFormState>(field: K, value: GenerationFormState[K]) => {
      setFormState((prev) => {
        const newState = { ...prev, [field]: value };

        // Reset dependent fields when discipline changes
        if (field === "discipline") {
          newState.challengeType = "";
          newState.targetSkills = [];
        }

        return newState;
      });
    },
    []
  );

  // Toggle skill tag
  const toggleSkill = useCallback((skill: SkillKey) => {
    setFormState((prev) => {
      const newSkills = prev.targetSkills.includes(skill)
        ? prev.targetSkills.filter((s) => s !== skill)
        : [...prev.targetSkills, skill];
      return { ...prev, targetSkills: newSkills };
    });
  }, []);

  // Generate content
  const generate = useCallback(async () => {
    if (!formState.discipline || !formState.challengeType) {
      setGenerationError("Please select a discipline and challenge type");
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setGenerationResult(null);
    setAcceptedChallenges({});

    try {
      const request: ContentGenerationRequest = {
        discipline: formState.discipline,
        level: formState.level,
        challengeType: formState.challengeType,
        count: formState.count,
        targetSkills: formState.targetSkills.length > 0 ? formState.targetSkills : undefined,
        customInstructions: formState.customInstructions || undefined,
      };

      const result = await apiPost<ContentGenerationResponse, ContentGenerationRequest>(
        "/api/admin/ai/generate",
        request
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (!result.data?.success) {
        throw new Error(result.data?.error || "Generation failed");
      }

      // Set all challenges as accepted by default
      const initialAccepted: ChallengeAcceptState = {};
      result.data.challenges.forEach((_, index) => {
        initialAccepted[index] = true;
      });

      setGenerationResult({
        challenges: result.data.challenges,
        metadata: result.data.metadata,
        allSkillTags: result.data.allSkillTags,
      });
      setAcceptedChallenges(initialAccepted);
    } catch (err) {
      console.error("Generation failed:", err);
      setGenerationError(err instanceof Error ? err.message : "Failed to generate content");
    } finally {
      setIsGenerating(false);
    }
  }, [formState]);

  // Toggle challenge acceptance
  const toggleChallengeAccepted = useCallback((index: number) => {
    setAcceptedChallenges((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  // Update skill tags on a generated challenge
  const updateChallengeSkillTags = useCallback((index: number, skillTags: SkillKey[]) => {
    setGenerationResult((prev) => {
      if (!prev) return null;
      const existingChallenge = prev.challenges[index];
      if (!existingChallenge) return prev;
      const newChallenges = [...prev.challenges];
      newChallenges[index] = { ...existingChallenge, skillTags };
      return { ...prev, challenges: newChallenges };
    });
  }, []);

  // Get accepted challenges count
  const getAcceptedCount = useCallback(() => {
    return Object.values(acceptedChallenges).filter(Boolean).length;
  }, [acceptedChallenges]);

  // Save accepted challenges
  const saveAccepted = useCallback(async () => {
    if (!generationResult || !formState.discipline) {
      setSaveError("No challenges to save");
      return null;
    }

    const acceptedIndices = Object.entries(acceptedChallenges)
      .filter(([, accepted]) => accepted)
      .map(([index]) => parseInt(index, 10));

    if (acceptedIndices.length === 0) {
      setSaveError("No challenges selected");
      return null;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // First, get the level ID for the selected discipline and level
      const levelsResult = await apiGet<LevelsResponse>(
        `/api/admin/levels?discipline=${formState.discipline}&level=${formState.level}`
      );

      if (levelsResult.error) {
        throw new Error(levelsResult.error.message);
      }

      const levels = levelsResult.data?.data || [];
      if (levels.length === 0) {
        throw new Error(`No level found for ${formState.discipline} level ${formState.level}`);
      }

      const level = levels[0];
      if (!level) {
        throw new Error(`No level found for ${formState.discipline} level ${formState.level}`);
      }
      const levelId = level.id;

      // Prepare challenges for bulk save
      const challengesToSave = acceptedIndices
        .map((index, i) => {
          const challenge = generationResult.challenges[index];
          if (!challenge) return null;
          return {
            levelId,
            challengeNumber: Date.now() + i, // Temporary unique number, will be updated by API
            type: challenge.type,
            content: challenge.content as unknown as Record<string, unknown>,
            difficulty: challenge.difficulty,
            skillTags: challenge.skillTags as string[],
            aiMetadata: generationResult.metadata as unknown as Record<string, unknown>,
            reviewStatus: "pending",
            isActive: false, // Start as inactive pending review
          };
        })
        .filter((c): c is NonNullable<typeof c> => c !== null);

      const saveResult = await apiPost<SaveChallengesResponse, SaveChallengesRequest>(
        "/api/admin/challenges/bulk",
        {
          challenges: challengesToSave,
          generationId: generationResult.metadata.generationRunId,
        }
      );

      if (saveResult.error) {
        throw new Error(saveResult.error.message);
      }

      if (!saveResult.data?.success) {
        throw new Error("Failed to save challenges");
      }

      return {
        savedCount: saveResult.data.data?.count || 0,
        createdIds: saveResult.data.data?.createdIds || [],
      };
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError(err instanceof Error ? err.message : "Failed to save challenges");
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [generationResult, formState.discipline, formState.level, acceptedChallenges]);

  // Reset form
  const reset = useCallback(() => {
    setFormState(initialFormState);
    setGenerationResult(null);
    setAcceptedChallenges({});
    setGenerationError(null);
    setSaveError(null);
  }, []);

  return {
    // Form state
    formState,
    updateField,
    toggleSkill,

    // Generation
    isGenerating,
    generationError,
    generate,

    // Results
    generationResult,
    acceptedChallenges,
    toggleChallengeAccepted,
    updateChallengeSkillTags,
    getAcceptedCount,

    // Saving
    isSaving,
    saveError,
    saveAccepted,

    // Reset
    reset,
  };
}
